import { InRow, LoadableContent, ResponsiveGap } from "common/Grid";
import { useState, useCallback, useEffect } from "react";
import { Checkbox, CheckboxMultiSelectInput, SelectInput } from "common/Forms/inputs";
import { useDailyTransactionSummaryMutation } from "store/action/admin-fee";
import { FaCaretLeft, FaCaretRight } from "react-icons/fa";
import { SecondaryButton } from "common/Forms";
import { Form, FormField } from "common/Forms"
import { Grid, GridCell } from "common/Grid/Grid"
import { LineChart } from "common/Chart";
import { ScreenSizes, useScreenSizes } from "common/Grid/useScreenSize";

export const DailyTransactionChart = ({ siteIdOptionProvider }) => {
  const [getSalesSummary, { isLoading }] = useDailyTransactionSummaryMutation();
  const [transactionData, setTransactionData] = useState();
  // const [currencyOptions, setCurrencyOptions] = useState([]);
  const [periodOptions, setPeriodOptions] = useState([]);
  const [period, setPeriod] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [selectSiteIds, setSelectSiteIds] = useState({ "siteIds": [] });
  const screenSize = useScreenSizes();

  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const earliestYear = 2022;
  const earliestMonth = 1;
  const lineColors = ["#766ff2", "#f58b89", "#4caf50"];
  const [showCumulative, setShowCumulative] = useState(false);

  const siteIdOptionProviderTrimmed = siteIdOptionProvider.map(item => {
    const labelParts = item.label.split(" - ");
    return {
      ...item,
      label: labelParts[1] || item.label
    };
  });

  const getPrevMonthYear = (m, y, periodVal) => {
    if (periodVal === "Monthly") return { pm: m, py: y - 1 };
    if (m === 1) return { pm: 12, py: y - 1 };
    return { pm: m - 1, py: y };
  };

  const GetSalesInfoAsync = useCallback(async () => {
    await getSalesSummary({ month, year, siteid: selectSiteIds.siteIds ?? ['10000'] })
      .unwrap()
      .then((result) => {
        const summary = result?.summary ?? {};
        if (result) {
          const currencies = Object.keys(summary).map(key => ({
            label: key,
            value: key
          }));
          // setCurrencyOptions(currencies);

          const firstCurrency = Object.keys(summary)[0];
          const summaryData = summary[firstCurrency] && !Array.isArray(summary[firstCurrency])
            ? summary[firstCurrency]
            : summary;

          if (summaryData) {
            const periods = Object.keys(summaryData)
              .filter(period => Object.keys(summaryData[period] || {}).length > 0)
              .map(period => ({
                label: period,
                value: period
              }));
            setPeriodOptions(periods);

            if (!selectedCurrency) {
              setSelectedCurrency(currencies[0]?.value || "");
            }

            if (!period) {
              setPeriod(periods[0]?.value || "");
            }
          }

          if (!currencies.find(c => c.value === selectedCurrency)) {
            setSelectedCurrency(currencies[0]?.value || "");
          }

          if ((!selectSiteIds.siteIds || selectSiteIds.siteIds.length <= 1) && period !== "Yearly") {
            const { pm, py } = getPrevMonthYear(month, year, period);
            getSalesSummary({ month: pm, year: py, siteid: selectSiteIds.siteIds ?? ['10000'] })
              .unwrap()
              .then(prevResult => {
                setTransactionData({ ...result, previousSummary: prevResult?.summary });
              })
              .catch(() => {
                setTransactionData(result);
              });
            return;
          }
        }
        setTransactionData(result);
      })
    // eslint-disable-next-line
  }, [getSalesSummary, month, selectSiteIds.siteIds, year]);

  useEffect(() => {
    GetSalesInfoAsync();
  }, [GetSalesInfoAsync]);

  const handlePrevMonth = () => {
    if (period === "Monthly") {
      setYear((y) => y - 1);
      return;
    }

    setMonth((prev) => {
      if (prev === 1) {
        setYear((y) => y - 1);
        return 12;
      }
      return prev - 1;
    });
  };

  const handleNextMonth = () => {
    if (period === "Monthly") {
      setYear((y) => y + 1);
      return;
    }

    setMonth((prev) => {
      if (prev === 12) {
        setYear((y) => y + 1);
        return 1;
      }
      return prev + 1;
    });
  };

  // const transformChartData = (data, currency, period) => {
  //   if (!data?.summary?.[currency]?.[period]) return [];
  //   return data.summary[currency][period].map(item => ({
  //     xValue: item.Date,
  //     sales: parseFloat((item.TotalTransactions || "0").replace(/[^\d.-]/g, "")) || 0,
  //     salesLabel: item.TotalTransactions,
  //     product: item.ProductQty,
  //   }));
  // };

  const transformChartData = (data, currency, period, sites) => {
    if (!data?.summary) return [];

    const valueKey = showCumulative ? "CumulativeTotal" : "TotalTransactions";
    const labelKey = valueKey;

    // When no sites selected, data is directly under period
    if (!sites?.length) {
      const periodData = data.summary[period];

      // if periodData is not array then return []
      if (!Array.isArray(periodData)) return [];

      const prevPeriod = data.previousSummary ? (data.previousSummary[period] || []) : [];

      return periodData.map((item, idx) => ({
        xValue: item.Date,
        sales_transactions: parseFloat(((item?.[valueKey] || "0").toString()).replace(/[^\d.-]/g, "")) || 0,
        salesLabel_transactions: item?.[labelKey] || "0",
        sales_transactions_prev: parseFloat(((prevPeriod[idx]?.[valueKey] || "0").toString()).replace(/[^\d.-]/g, "")) || 0,
        salesLabel_transactions_prev: prevPeriod[idx]?.[labelKey] || "0"
      }));
    }

    const dates = data.summary[sites[0]]?.[period]?.map(d => d.Date) || [];
    return dates.map(date => {
      const row = { xValue: date };

      sites.forEach(site => {
        const d = data.summary[site]?.[period]?.find(x => x.Date === date);
        const transaction = parseFloat(((d?.[valueKey] || "0").toString()).replace(/[^\d.-]/g, "")) || 0;

        // row.sales_10000 is for static and row[`sales_${site}`] for dynamic
        row[`sales_${site}`] = transaction;
        row[`salesLabel_${site}`] = d?.[labelKey] || "0";

        if (data.previousSummary && data.previousSummary[site] && Array.isArray(data.previousSummary[site][period])) {
          const prevArr = data.previousSummary[site][period];
          const prevByDate = prevArr.find(x => x.Date === date);
          const prevIdxItem = prevByDate || prevArr[dates.indexOf(date)] || {};
          row[`sales_${site}_prev`] = parseFloat(((prevIdxItem?.[valueKey] || "0").toString()).replace(/[^\d.-]/g, "")) || 0;
          row[`salesLabel_${site}_prev`] = prevIdxItem?.[labelKey] || "0";
        }
      });
      return row;
    });
  };

  const chartData = transformChartData(transactionData, selectedCurrency, period, selectSiteIds.siteIds, showCumulative);

  const getPeriodOptions = useCallback(async () => periodOptions, [periodOptions]);


  return (
    <Form>
      <LoadableContent isLoading={isLoading}>
        <ResponsiveGap classNames={['px-0']}>
          <Grid columns={screenSize === ScreenSizes.Large ? 4 : screenSize === ScreenSizes.Medium ? 4 : 1}>
            <GridCell columnSpan={screenSize === ScreenSizes.Small ? 1 : 2}>
              <Grid columns={screenSize === ScreenSizes.Large ? 2 : screenSize === ScreenSizes.Medium ? 1 : 1} classNames={[screenSize === ScreenSizes.Small ? '!gap-y-2' : '']}>
                <GridCell vertical='center'>
                  <FormField
                    id="period-select"
                    label="Period:"
                    direction="row"
                    classNames={['items-center chart_dropdown_full', screenSize === ScreenSizes.Small ? 'flex-wrap gap-0' : 'gap-2']}
                    inputFactory={(id, name) =>
                      <SelectInput
                        disabled={false}
                        id={id}
                        name={name}
                        placeholder="Select Period"
                        selectedKey={period}
                        isClearable={false}
                        optionProvider={getPeriodOptions}
                        keySelector={(x) => x.value}
                        labelSelector={(x) => x.label}
                        onChange={(_, n, selector) => n ? setPeriod(selector(n)) : undefined} />
                    } />
                </GridCell>
                <GridCell vertical='center'>
                  <FormField
                    id="currency"
                    label="Site ID:"
                    direction="row"
                    classNames={['items-center chart_dropdown_full [&>label]:whitespace-nowrap', screenSize === ScreenSizes.Small ? 'flex-wrap gap-0' : 'gap-2']}
                    inputFactory={(id, name) =>
                      <CheckboxMultiSelectInput
                        id={id} name={name}
                        placeholder={'Select Site ids'}
                        selectedKeys={selectSiteIds.siteIds}
                        optionProvider={() => siteIdOptionProvider}
                        keySelector={(x => x.value)}
                        labelSelector={(x => x.label)}
                        onChange={(_, next) => {
                          if ((next?.length || 0) <= 3) {
                            setSelectSiteIds({ ...selectSiteIds, siteIds: next || [] });
                          }
                        }} />
                    } />
                </GridCell>
              </Grid>
            </GridCell>
            {screenSize !== ScreenSizes.Small && <GridCell />}
            <GridCell vertical='center'>
              <InRow gap={2} classNames={['justify-end']}>
                <InRow gap={1} classNames={['flex-nowrap']}>
                  <label htmlFor="checkbox">Cumulative</label>
                  <Checkbox
                    id="checkbox"
                    value={showCumulative}
                    onChange={(val) => setShowCumulative(val)}
                  />
                </InRow>
                <InRow gap={1} classNames={['justify-end']}>
                  <SecondaryButton
                    title="prev"
                    action={handlePrevMonth}
                    disabled={period === "Monthly" ? year === earliestYear : (year === earliestYear && month === earliestMonth)}>
                    <FaCaretLeft size={20} />
                  </SecondaryButton>
                  <SecondaryButton
                    title="next"
                    action={handleNextMonth}
                    disabled={period === "Monthly" ? year === today.getFullYear() : (year === today.getFullYear() && month === today.getMonth() + 1)}>
                    <FaCaretRight size={20} />
                  </SecondaryButton>
                </InRow>
              </InRow>
            </GridCell>
          </Grid>
        </ResponsiveGap>

        <div style={{ width: "100%", overflowX: "auto" }}>
          <div style={{ minWidth: "1500px", height: screenSize === ScreenSizes.Small ? 400 : 600 }}>
            <LineChart
              data={chartData}
              selectedPeriod={period}
              description={{
                axes: {
                  x: {
                    x: { dataKey: "xValue", style: { showLabels: true, showTitle: false } }
                  },
                  y: {
                    y: {
                      style: { showLabels: true, showTitle: false },
                      tickFormatter: (value) => {
                        const summaryData = selectSiteIds.siteIds?.length
                          ? transactionData?.summary?.[selectSiteIds.siteIds[0]]
                          : transactionData?.summary;

                        const totalString = Array.isArray(summaryData?.[period])
                          ? summaryData[period][0]?.TotalTransactions || ""
                          : summaryData?.[Object.keys(summaryData || {})[0]]?.[period]?.[0]?.TotalTransactions || "";

                        const match = typeof totalString === 'string' ? totalString.match(/^[^\d.-]+/) : null;
                        const symbol = match ? match[0] : "";
                        return symbol + value;
                      }
                    }
                  }
                },
                tooltipFormatter: (value, name, props) => {
                  const siteObj = siteIdOptionProviderTrimmed.find(s => s.label === name);
                  if (siteObj) {
                    const siteId = siteObj.value;
                    const salesLabel = props.payload[`salesLabel_${siteId}`] || value;
                    return [salesLabel, name];
                  }
                  return [`${value}`, name];
                },
                data: (selectSiteIds.siteIds?.length > 0 ? selectSiteIds.siteIds : ['transactions']).flatMap((site, i) => {
                  const siteLabel = siteIdOptionProviderTrimmed.find(s => s.value === site)?.label || site;
                  const base = {
                    dataKey: `sales_${site}`,
                    title: showCumulative ? `${siteLabel} (Cumulative)` : siteLabel,
                    xAxisKey: "x",
                    yAxisKey: "y",
                    style: { color: lineColors[i] },
                  };
                  if (!showCumulative && (selectSiteIds.siteIds?.length || 0) <= 1 && period !== "Yearly") {
                    const prev = {
                      dataKey: `sales_${site}_prev`,
                      title: `${siteLabel} (Prev)`,
                      xAxisKey: "x",
                      yAxisKey: "y",
                      style: { color: lineColors[i % lineColors.length] + "60" },
                    };
                    return [base, prev];
                  }
                  return [base];
                })
              }}
              showGrid={true}
              showTooltip={true}
              showLegend={true}
            />
          </div>
        </div>
      </LoadableContent>
    </Form >
  );
};
